import { IMeeting } from "@mctypes/meeting.types";
import { IIssueApi } from "@schemas/issueSchema";
import React from "react";
import { useHistory } from "react-router-dom";
import { usePutProjectByProjectIdAgendaItemReorderConnectedIssuesMutation } from "../../../../../../openapi";
import { FailedAlert } from "../../../../../components";
import { setShowFailedMessageThunk } from "../../../../../global/globalSlice";
import { ConnectedIssues } from "../../../../../components/ConnectedIssues/ConnectedIssues";
import CreateItemWithBoardSelectorModal from "../../../../../components/CreateItemWithBoardSelectorModal/CreateItemWithBoardSelectorModal";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { MCApplicationsUrlEnum } from "../../../../../mc-shared/sharedConstants";
import ConnectExistingIssueToItemModalWrapper from "../../../../ConnectExistingIssueToItemModalWrapper";
import { getProjectIdFromUrl } from "../../../../utils";
import { useGetProjectByProjectIdIssueboardQuery } from "../../../../../../openapi";
import { useCreateIssueMutation } from "../../../issuesApp/issueRTKLegacy";
import { useConnectOrDisconnectIssueFromAgendaItemMutation } from "../../meetingAgendaRTK";

const AgendaConnectedIssues: React.FC<{
  agendaItem: IMeeting.AgendaItem;
  defaultIssueBoard?: { name: string; _id: string };
}> = ({ agendaItem, defaultIssueBoard }) => {
  const [newIssueModalOpen, setNewIssueModalOpen] = React.useState<boolean>(false);
  const [connectExistingModalOpen, setConnectExistingModalOpen] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();

  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);

  const { data: issueBoards } = useGetProjectByProjectIdIssueboardQuery({
    projectId: useAppSelector((state) => state.adminReducer.project._id),
  });
  const activeIssueBoards = React.useMemo(() => (issueBoards ?? []).filter((board) => board.status === "ACTIVE"), [
    issueBoards,
  ]);
  const activeDefaultIssueBoard = React.useMemo(
    () => {
      const board = activeIssueBoards.find((board) => board._id === defaultIssueBoard?._id);
      return board?._id != null && board.name != null ? { _id: board._id, name: board.name } : undefined;
    },
    [activeIssueBoards, defaultIssueBoard?._id],
  );

  const [connectOrDisconnect, { isError: connectOrDiscionnectError }] =
    useConnectOrDisconnectIssueFromAgendaItemMutation();
  const [createIssue, { isError: createIssueError }] = useCreateIssueMutation();
  const [putAgendaItemReorderConnectedIssues] = usePutProjectByProjectIdAgendaItemReorderConnectedIssuesMutation();
  const apps = useAppSelector((state) => state.globalReducer.apps);
  const project = useAppSelector((state) => state.adminReducer.project);
  const isError = connectOrDiscionnectError || createIssueError;
  const connectedIssues = agendaItem.connectedIssues ?? [];

  const onCreateIssue = async (boardId: string) => {
    await createIssue({
      projectId: projectId,
      issueBoardId: boardId,
      issueTitle: "",
      taskOrIssue: "ISSUE",
      connectedMeetingAgendaItem: agendaItem._id,
    });
    setNewIssueModalOpen(false);
  };

  const onConnect = async (boardId: string, issueId: string) => {
    try {
      await connectOrDisconnect({
        projectId: projectId,
        agendaId: agendaItem._id,
        issueId: issueId,
        connectOrDisconnect: "CONNECT",
      }).unwrap();

      setConnectExistingModalOpen(false);
    } catch (error) {
      const message =
        (error as any)?.data?.message ??
        (error as any)?.data?.error?.message ??
        "Kunne ikke koble saken til moteagendaen.";
      dispatch(setShowFailedMessageThunk(message));
    }
  };

  return (
    <>
      {isError === true && <FailedAlert />}
      <ConnectExistingIssueToItemModalWrapper
        opened={connectExistingModalOpen}
        defaultIssueBoard={activeDefaultIssueBoard}
        onConnected={(boardId, issueId) => onConnect(boardId, issueId)}
        projectId={projectId}
        closeModal={() => setConnectExistingModalOpen(false)}
        blockedIssueIds={connectedIssues
          .filter(Boolean)
          .map((issue) => (typeof issue === "string" ? issue : issue._id))
          .filter(Boolean)}
      />
      <CreateItemWithBoardSelectorModal
        defaultIssueBoard={activeDefaultIssueBoard}
        boards={activeIssueBoards.map((board) => {
          return {
            project: board.project,
            name: board.name,
            _id: board._id,
            status: board.status,
          };
        })}
        onCreate={onCreateIssue}
        modalTitle="Koble sak til møte"
        opened={newIssueModalOpen}
        onClose={() => setNewIssueModalOpen(false)}
        currentApp={MCApplicationsUrlEnum.ISSUE}
        apps={apps}
        projectId={project._id}
        createButtonText="Opprett sak"
      />
      <ConnectedIssues
        defaultIssueBoard={activeDefaultIssueBoard}
        onCreateNewFromDefault={(id) => onCreateIssue(id)}
        issues={(connectedIssues as IIssueApi[]).filter((issue) => issue != null && issue.archived !== true)}
        onCreateNewIssue={() => setNewIssueModalOpen(true)}
        onDisconnectIssue={(issueId) =>
          connectOrDisconnect({
            projectId: projectId,
            agendaId: agendaItem._id,
            issueId: issueId,
            connectOrDisconnect: "DISCONNECT",
          })
        }
        onConnectExistingIssue={() => setConnectExistingModalOpen(true)}
        isPrintMode={false}
        titleLabel="Koblede saker"
        projectId={projectId}
        enableReorder={true}
        agendaItemId={agendaItem._id}
        updateOrderOfItems={({ projectId, newOrder }) =>
          putAgendaItemReorderConnectedIssues({
            projectId,
            body: {
              agendaItemId: agendaItem._id,
              newOrder,
            },
          })
        }
      />
    </>
  );
};

export default AgendaConnectedIssues;


